/* global mainScript */
import React, { useState, Suspense, useRef } from "react";
import { useMediaQuery } from "react-responsive";
import { motion, AnimatePresence } from "framer-motion";
import styled from "@emotion/styled";
import PropTypes from "prop-types";
import dayjs from "dayjs";

//hooks
import useAxios from "../../hooks/useAxios";
import useSortContent from "../../hooks/useSortContent";

//comps
const PageGrid = React.lazy(() => import("../PageGrid"));
const EventCard = React.lazy(() => import("./EventCard"));
const DateSelector = React.lazy(() => import("./DateSelector"));
const NoResults = React.lazy(() => import("../NoResults"));
import FiltersPanel from "../Schedule/FiltersPanel";
import Loading from "../Loading";

//style rules
import { Colors, Sizes, Typography } from "../../styles";

const App = ({ pageTitle, datesJson, filtersJson, note, pdfUrl }) => {
	let requestDateFormate = "YYYYMMDD";
	let displayDateFormate = "MMMM-D";

	const getInitialDate = (date) => {
		let now = dayjs().toDate();

		if (dayjs(now).isAfter(date, "day")) {
			return dayjs(now).toDate();
		}

		return dayjs(date).toDate();
	};

	const [openFiltersPanel, setOpenFiltersPanel] = useState(false);
	const [dates] = useState(datesJson ? JSON.parse(datesJson) : {});
	const [filters] = useState(filtersJson ? JSON.parse(filtersJson) : {});
	const [selectedDate, setSelectedDated] = useState(
		getInitialDate(dates.start)
	);
	const [selectedFilters, setSelectedFilters] = useState([]);
	const [endPoint, setEndPoint] = useState(
		`events?day=${dayjs(selectedDate).format(requestDateFormate)}`
	);

	const events = useAxios(endPoint);

	const sortedEvents = useSortContent(events, "date");

	const scrollRef = useRef(null);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	let displayDate = dayjs(selectedDate)
		.format(displayDateFormate)
		.replace("-", " ");

	const handleDateSelect = (date) => {
		// selected gets set and passed down to the date picker
		setSelectedDated(date);
		let formattedDate = dayjs(date).format(requestDateFormate);
		setEndPoint(`events?day=${formattedDate}`);

		return !isDesktopOrLaptop && scrollRef.current.scrollIntoView();
	};

	const toggleFiltersPanel = () => {
		setOpenFiltersPanel(!openFiltersPanel);
	};

	const filterEvents = () => {
		return sortedEvents.filter((event) =>
			selectedFilters.some(
				(sf) => sf.slug == event.locationSlug || sf.slug == event.type
			)
		);
	};

	const handleFilterSelect = (filter) => {
		let selectedFiltersCopy = [...selectedFilters];
		let existingFilterIndex = selectedFiltersCopy.findIndex(
			(f) => f.slug == filter.slug
		);

		if (existingFilterIndex >= 0) {
			selectedFiltersCopy = selectedFiltersCopy.filter(
				(f) => f.slug !== filter.slug
			);
		} else {
			selectedFiltersCopy = [...selectedFiltersCopy, filter];
		}

		setSelectedFilters(selectedFiltersCopy);
	};

	const clearFilters = () => {
		toggleFiltersPanel();
		return setSelectedFilters([]);
	};

	const renderCards = (eventsArr) => {
		return eventsArr.map((event) => {
			return <EventCard event={event} key={event.id} />;
		});
	};

	const renderEvents = () => {
		if (sortedEvents.length == 0) {
			return (
				<NoResults
					color={Colors.base.white}
					text={"Stay tuned for upcoming events"}
				/>
			);
		}

		if (selectedFilters && selectedFilters.length == 0) {
			return renderCards(sortedEvents);
		}

		const filteredEvents = filterEvents();
		if (filteredEvents && filteredEvents.length == 0) {
			return (
				<NoResults
					color={Colors.base.white}
					text={"No events matching your search"}
				/>
			);
		}

		return renderCards(filteredEvents);
	};

	const renderSelectedFilters =
		selectedFilters &&
		selectedFilters.length > 0 &&
		selectedFilters.map((filter) => {
			return (
				<Selected onClick={() => handleFilterSelect(filter)} key={filter.slug}>
					{filter.name + " x"}
				</Selected>
			);
		});

	return (
		<section aria-label="CNE Schedule" >
			<Suspense fallback={<Loading />}>
				<PageGrid>
					<Title>{pageTitle}</Title>
					<Grid className="pin-parent">
						<GridColumn>
							<DateSelector
								dates={dates}
								selectedDate={selectedDate}
								handleDateSelect={handleDateSelect}
								note={note}
								pdfUrl={pdfUrl}
							/>
						</GridColumn>
						<GridColumn ref={scrollRef}>
							<ScheduleHeader>
								<ScheduleTitle>{`What’s on: ${displayDate} `}</ScheduleTitle>
								<FiltersButton
									layoutId="expandable-panel"
									onClick={toggleFiltersPanel}
									onKeyPress={toggleFiltersPanel}
								>
									Filter Events
								</FiltersButton>
								{openFiltersPanel && (
									<AnimatePresence>
										<FiltersPanel
											toggleFiltersPanel={toggleFiltersPanel}
											filters={filters}
											handleFilterSelect={handleFilterSelect}
											selectedFilters={selectedFilters}
											clearFilters={clearFilters}
										/>
									</AnimatePresence>
								)}
							</ScheduleHeader>
							{renderSelectedFilters && (
								<SelectedFilters>
									Filters: {renderSelectedFilters}
								</SelectedFilters>
							)}
							<Events>{renderEvents()}</Events>
						</GridColumn>
					</Grid>
				</PageGrid>
			</Suspense>
		</section>
	);
};

export default App;

App.propTypes = {
	pageTitle: PropTypes.string,
	datesJson: PropTypes.string,
	filtersJson: PropTypes.string,
	note: PropTypes.string,
	pdfUrl: PropTypes.string,
};

const Title = styled.h1`
	color: ${Colors.base.white};
	text-align: center;
	padding-top: 7rem;
`;

const Grid = styled.div`
	display: grid;
	grid-template-columns: 2fr;
	gap: 5rem;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		grid-template-columns: 490px 2fr;
		padding-top: 6.7rem;
	}
`;

const GridColumn = styled.div``;

const ScheduleHeader = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: fit-content;
	flex-wrap: wrap;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		flex-direction: row;
		padding-top: 1.8rem;
	}
`;

const ScheduleTitle = styled.h2`
	font-size: 2rem;
	line-height: 4.6rem;
	letter-spacing: -0.0095em;
	color: ${Colors.base.white};
	white-space: pre;

	@media only screen and (min-width: ${Sizes.screens.large}) {
		font-size: 3rem;
		line-height: 6.6rem;
	}
`;

const FiltersButton = styled(motion.button)`
	width: fit-content;
	position: relative;
	font-size: 1.4rem;
	letter-spacing: -0.0095em;
	color: ${Colors.base.white};
	background: none;
	border-radius: 10px;
	border: 1px solid ${Colors.base.white};
	height: 5rem;
	padding: 1.3rem 1.4rem;
	display: flex;
	gap: 3.6rem;
	align-items: center;
	transition: all 0.5s;

	&::after {
		content: url(${mainScript.assetPath + "images/svg/icons/icon-filters.svg"});
		height: 20px;
	}

	&:hover,
	&:focus {
		transition: all 0.5s;
		background: ${Colors.base.white};
		color: ${Colors.base.black};
	}
`;

const SelectedFilters = styled.div`
	display: flex;
	gap: 1.4rem;
	color: ${Colors.base.white};
	font-weight: ${Typography.fontWeight.bold};
	font-size: 1.4rem;
	align-items: center;
`;

const Selected = styled.p`
	cursor: pointer;
	color: ${Colors.base.black};
	background-color: ${Colors.base.white}90;
	font-weight: ${Typography.fontWeight.bold};
	padding: 0.5rem;
	font-size: 1.2rem;
	line-height: 0.7rem;
	border-radius: 3px;
`;

const Events = styled.div`
	display: ${(props) => (Array.isArray(props.children) ? "grid" : "block")};
	grid-template-columns: 1fr;
	gap: 2.3rem;
	padding-top: 4rem;
	justify-items: center;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
	}
`;
